import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../../components/auth/useAuth'
import { getProducts, getProduct, addProduct, editProduct, fillProductWithAI } from '../../../actions/productsActions'
import { CustomDataTable } from '../../../components/design/Datatables/CustomDataTable'
import Loader from '../../../components/loaders/Loader'
import ProductForm from '../../../components/forms/ProductForm'
import Swal from 'sweetalert2'
import ProductViewModal from './ProductsDetails'
import ImportProductsModal from '../../../components/modals/ImportProductsModal'
import getProxiedImageUrl from '../../../helpers/imageProxy'

const formatDepartments = (departments = []) => {
    return departments.map(department => {
        if (typeof department === 'string') {
            return department
        }

        return department?.name || department?.code || ''
    }).filter(Boolean).join(', ')
}

const normalizeDepartments = (departments = []) => {
    return [...new Set(
        departments
            .map(department => String(department || '').trim().toUpperCase())
            .filter(Boolean)
    )]
}

export default function Products() {
    const { t } = useTranslation()
    const location = useLocation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()

    const { products, loading, resultproduct, total } = useSelector(state => state.products)

    const [modalShow, setModalShow] = useState(false)
    const [formState, setFormState] = useState({})
    const [isRegistering, setIsRegistering] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    const [selectedProduct, setSelectedProduct] = useState(null)
    const [selectedProductFull, setSelectedProductFull] = useState(null)

    const [detailModalShow, setDetailModalShow] = useState(false)
    const [importModalShow, setImportModalShow] = useState(false)

    const [aiFillLoading, setAiFillLoading] = useState(false)

    const handleAiFill = async (product) => {
        setAiFillLoading(true)
        try {
            const result = await dispatch(fillProductWithAI(product._id))

            const editPayload = {}
            if (result?.filledAttributes?.length) editPayload.attributes = result.filledAttributes
            if (result?.sources?.length) editPayload.sources = result.sources
            if (result?.imageUrl && !product.imageUrl) editPayload.imageUrl = result.imageUrl
            if (result?.pdfUrl && !product.pdfUrl) editPayload.pdfUrl = result.pdfUrl

            if (Object.keys(editPayload).length > 0) {
                await dispatch(editProduct(product._id, editPayload))
            }

            const updated = await dispatch(getProduct(product._id))
            if (updated) {
                setSelectedProduct(updated)
                setSelectedProductFull(updated)
            }
        } catch (err) {
            console.error(err)
        }
        setAiFillLoading(false)
    }

    const handleSaveDocs = async (docsData) => {
        const product = selectedProductFull || selectedProduct
        if (!product) return
        await dispatch(editProduct(product._id, docsData))
        const updated = await dispatch(getProduct(product._id))
        if (updated) {
            setSelectedProductFull(updated)
            setSelectedProduct(updated)
        }
    }

    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(25)
    const [search, setSearch] = useState('')
    const [sortBy, setSortBy] = useState(null)
    const [sortOrder, setSortOrder] = useState(null)

    useEffect(() => {
        if (clientauth) {
            dispatch(getProducts(page, limit, search, sortBy, sortOrder))
        }
    }, [dispatch, clientauth, location, page, limit, search, sortBy, sortOrder])

    const openModal = () => {
        setIsEditing(false)
        setFormState({
            reference: '',
            name: '',
            description: '',
            brand: '',
            departments: normalizeDepartments(clientauth?.departments || []).slice(0, 1),
            attributes: {}
        })
        setModalShow(true)
    }

    const openDetailModal = async (product) => {
        setSelectedProduct(product)
        setDetailModalShow(true)
        try {
            const fullProduct = await dispatch(getProduct(product._id))
            if (fullProduct) {
                setSelectedProductFull(fullProduct)
            }
        } catch (e) {
            // fallback to list data
        }
    }

    const selectProduct = (product) => {
        setSelectedProduct(product)
        setDetailModalShow(false)
        setFormState(product)
        setIsEditing(true)
        setModalShow(true)
    }

    const handleInputChange = ({ target }) => {
        setFormState({
            ...formState,
            [target.name]: target.value
        })
    }

    const handleSubmit = async (productData) => {
        setIsRegistering(true)
        if (isEditing) {
            await dispatch(editProduct(formState._id, productData))
        } else {
            await dispatch(addProduct(productData))
        }
        setIsRegistering(false)
    }

    const refreshProducts = () => {
        dispatch(getProducts(page, limit, search, sortBy, sortOrder))
    }

    const handlePageChange = (newPage) => {
        setPage(newPage)
    }

    const handleSort = (column, sortDirection) => {
        if (column.id) {
            setSortBy(column.id)
            setSortOrder(sortDirection)
        }
    }

    const handleRowsPerPageChange = (newLimit) => {
        setLimit(newLimit)
        setPage(1)
    }

    const closeModal = () => {
        setModalShow(false)
    }

    const closeDetailModal = () => {
        setDetailModalShow(false)
        setSelectedProductFull(null)
    }

    useEffect(() => {
        if (resultproduct) {
            console.log(resultproduct)
            dispatch({ type: 'PRODUCT_RESET_RESULT' })
            switch (resultproduct) {
                case 'productCreated':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.products.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                        refreshProducts()
                    })
                    break;
                case 'productUpdated':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.products.editsuccess')
                    }).then(() => {
                        setModalShow(false)
                        refreshProducts()
                    })
                    break;
                case 'aiFillReady':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.products.aiFillSuccess')
                    }).then(() => {
                        refreshProducts()
                    })
                    break;
                case 'aiFillUnavailable':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'warning',
                        text: t('admin.products.aiFillUnavailable')
                    })
                    break;
                case 'productsImported':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.products.importsuccess')
                    }).then(() => {
                        setModalShow(false)
                        refreshProducts()
                    })
                    break;
                default:
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'info',
                        text: resultproduct
                    })
                    break;
            }
        }
    }, [resultproduct, dispatch, t])

    const columnsProducts = [
        {
            id: 'image',
            name: '',
            width: '60px',
            minWidth: '60px',
            cell: product => {
                const img = (product.mainImage && product.mainImage !== '')
                    ? product.mainImage
                    : product.images?.[0]?.url
                        || product.imageUrl
                        || null
                return (
                    <div
                        className='cursor'
                        onClick={() => openDetailModal(product)}
                        style={{ width: '40px', height: '40px', borderRadius: '4px', overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                        {img ? (
                            <img src={getProxiedImageUrl(img)} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <i className="fas fa-box" style={{ color: '#ccc', fontSize: '0.9rem' }}></i>
                        )}
                    </div>
                )
            },
            sortable: false,
            ignoreRowClick: true,
        },
        {
            id: 'reference',
            name: t('admin.products.reference'),
            minWidth: '100px',
            cell: product =>
                <div className='cursor' onClick={() => openDetailModal(product)}>
                    {product.reference}
                </div>,
            selector: (product) => product.reference,
            sortable: true,
        },
        {
            id: 'name',
            name: t('admin.products.name'),
            minWidth: '140px',
            cell: product =>
                <div className='cursor' onClick={() => openDetailModal(product)}>
                    {product.name}
                </div>,
            selector: (product) => product.name,
            sortable: true,
        },
        {
            id: 'brand',
            name: t('admin.products.brand'),
            minWidth: '90px',
            cell: product =>
                <div className='cursor' onClick={() => openDetailModal(product)}>
                    {product.brand}
                </div>,
            selector: (product) => product.brand,
            sortable: true,
            hide: 'sm',
        },
        {
            id: 'group',
            name: t('admin.products.group'),
            minWidth: '80px',
            cell: product =>
                <div className='cursor' onClick={() => openDetailModal(product)}>
                    {product.group || '-'}
                </div>,
            selector: (product) => product.group,
            sortable: true,
            hide: 'md',
        },
        {
            id: 'subgroup',
            name: t('admin.products.subgroup'),
            minWidth: '80px',
            cell: product =>
                <div className='cursor' onClick={() => openDetailModal(product)}>
                    {product.subgroup || '-'}
                </div>,
            selector: (product) => product.subgroup,
            sortable: true,
            hide: 'md',
        },
        {
            name: t('admin.products.departments'),
            minWidth: '120px',
            cell: product =>
                <div className='cursor' onClick={() => openDetailModal(product)}>
                    {formatDepartments(product.departments)}
                </div>,
            selector: (product) => formatDepartments(product.departments),
            sortable: false,
            hide: 'sm',
        },
    ]

    return (
        <>
            <div className="toolbar">
                <div className="toolbar-left">
                    <div className="input-group" style={{ maxWidth: '320px' }}>
                        <span className="input-group-text"><i className="fas fa-search"></i></span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={t('admin.products.searchProducts')}
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1) }}
                        />
                    </div>
                </div>
                <div className="toolbar-right">
                    <Button variant="primary" onClick={() => setImportModalShow(true)}>
                        <i className="fas fa-file-import"></i>{t('admin.products.import')}
                    </Button>
                    <Button variant="success" onClick={openModal}>
                        <i className="fas fa-plus"></i>{t('admin.products.add')}
                    </Button>
                </div>
            </div>

                <div className="card-simple">
                    <CustomDataTable
                        columns={columnsProducts}
                        data={products}
                        pagination={true}
                        exportable={true}
                        printable={true}
                        filter={false}
                        paginationServer={true}
                        paginationTotalRows={total}
                        paginationDefaultPage={page}
                        paginationPerPage={limit}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onSort={handleSort}
                        progressPending={loading}
                        progressComponent={<div className="text-center p-4"><Loader /></div>}
                        onRowClicked={(row) => openDetailModal(row)}
                    />
            </div>
            <Modal show={modalShow} onHide={closeModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? t('admin.products.edit') : t('admin.products.add')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProductForm
                        key={`${isEditing ? formState._id : 'new'}-${modalShow}`}
                        state={formState}
                        handleInputChange={handleInputChange}
                        handleSubmit={handleSubmit}
                        isRegistering={isRegistering}
                        onClose={closeModal}
                        currentUser={clientauth}
                    />
                </Modal.Body>
            </Modal>
            <Modal show={detailModalShow} onHide={closeDetailModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedProductFull?.reference || selectedProduct?.reference || selectedProductFull?.name || selectedProduct?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ProductViewModal
                        show={detailModalShow}
                        product={selectedProductFull || selectedProduct}
                        t={t}
                        onAiFill={() => handleAiFill(selectedProductFull || selectedProduct)}
                        aiFillLoading={aiFillLoading}
                        onEdit={selectProduct}
                        onSaveDocuments={handleSaveDocs}
                    />
                </Modal.Body>
            </Modal>
            <ImportProductsModal show={importModalShow} onHide={() => setImportModalShow(false)} onImportComplete={() => dispatch(getProducts(page, limit, search, sortBy, sortOrder))} />
        </>
    )
}
